import { Lock, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useConsultations } from '@/hooks/useConsultations'
import { useAuthStore } from '@/hooks/useAuthStore'
import { usePermissions } from '@/hooks/usePermissions'
import { ClarifyingNotesSection } from './ClarifyingNotesSection'
import { VITALS, toNumericVital } from '@/lib/vitals'

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'En curso',
  completed: 'Completada',
  locked: 'Bloqueada',
  unlock_requested: 'Desbloqueo solicitado',
}

const SIGNED_STATUSES = new Set(['completed', 'locked', 'unlock_requested'])

interface ConsultationNoteDialogProps {
  /** The note to open, or null when closed. */
  consultationId: string | null
  onClose: () => void
}

/**
 * Read-only viewer for a single signed progress note (REQ-FE-03).
 *
 * Opens the EXACT note referenced (from the timeline or a trend point), reading
 * the full record from the single note cache (`['consultation', id]`) — no
 * local copy (REQ-FE-05). Shows the full §6.2 content, structured vitals, the
 * signing author + integrity hash, and the linked clarifying notes (addenda).
 *
 * The note itself is NEVER editable here (REQ-FE-09 / §9). The only sanctioned
 * way to "modify" a signed note is a clarifying note (nota aclaratoria), offered
 * inline via ClarifyingNotesSection — it appends a linked correction and never
 * touches the original (REQ-INT-03).
 */
export function ConsultationNoteDialog({ consultationId, onClose }: ConsultationNoteDialogProps) {
  const { useConsultation } = useConsultations()
  const { data: note, isLoading } = useConsultation(consultationId ?? '')
  const user = useAuthStore((s) => s.user)
  const { isOwner } = usePermissions()

  const isSigned = !!note && SIGNED_STATUSES.has(note.status)
  const isAuthor = !!user && !!note && note.doctor_id === user.id
  const isAdmin = isOwner || user?.role === 'admin' || user?.role === 'super_admin'
  // Only the signing author or an admin may append an addendum to a signed note.
  const canAddAddendum = isSigned && (isAuthor || isAdmin)

  const vitals = note?.vitals ?? {}
  const presentVitals = VITALS.map((v) => ({ ...v, value: toNumericVital(vitals[v.key]) })).filter(
    (v) => v.value != null,
  )

  return (
    <Dialog open={!!consultationId} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-headline italic text-ml-primary">
            Nota de evolución
          </DialogTitle>
          {note && (
            <div className="flex items-center gap-2 text-xs text-ml-on-surface-variant mt-1">
              <Lock className="h-3 w-3" />
              <span>
                {new Date(note.created_at).toLocaleString('es-MX')} · Solo lectura ·{' '}
                {STATUS_LABELS[note.status] ?? note.status}
              </span>
            </div>
          )}
        </DialogHeader>

        {isLoading || !note ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-ml-primary" />
          </div>
        ) : (
          <div className="space-y-5 mt-4">
            {note.chief_complaint && <Field label="Motivo de consulta" value={note.chief_complaint} />}
            {note.subjective_notes && <Field label="Subjetivo (S)" value={note.subjective_notes} />}
            {note.objective_notes && <Field label="Objetivo (O)" value={note.objective_notes} />}

            {presentVitals.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-2">
                  Signos vitales
                </p>
                <div className="flex flex-wrap gap-2">
                  {presentVitals.map((v) => (
                    <span
                      key={v.key}
                      className="rounded-md bg-ml-surface-container px-2.5 py-1 text-xs text-ml-on-surface"
                    >
                      <span className="text-ml-on-surface-variant/70">{v.label}: </span>
                      {v.value}
                      <span className="text-ml-on-surface-variant/50"> {v.unit}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(note.diagnosis_codes?.length ?? 0) > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-2">
                  Diagnósticos
                </p>
                <div className="flex flex-wrap gap-2">
                  {note.diagnosis_codes.map((dx) => (
                    <span
                      key={dx}
                      className="rounded bg-ml-primary-fixed px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ml-primary"
                    >
                      {dx}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {note.assessment && <Field label="Valoración clínica (A)" value={note.assessment} />}
            {note.plan_notes && <Field label="Tratamiento y plan terapéutico (P)" value={note.plan_notes} />}
            {note.prognosis && <Field label="Pronóstico" value={note.prognosis} />}

            {/* Signing author + integrity hash (REQ-FE-01 / REQ-NOTE-02) */}
            {note.attested_by_name && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900 space-y-1">
                <div>
                  <span className="font-semibold">Firmada por:</span> {note.attested_by_name}
                  {note.attested_by_cedula && <> · Cédula profesional {note.attested_by_cedula}</>}
                  {note.attested_at && <> · {new Date(note.attested_at).toLocaleString('es-MX')}</>}
                </div>
                {note.content_hash && (
                  <div className="text-[11px] font-mono text-emerald-700/80 break-all">
                    <span className="font-semibold">Hash de integridad (SHA-256):</span> {note.content_hash}
                  </div>
                )}
              </div>
            )}

            {/* "Modify" a signed note the legal way: append a linked clarifying
                note. Shows existing addenda + (for author/admin) a one-click
                "Agregar" shortcut. The original is never edited (REQ-FE-04 / §9). */}
            {isSigned && (
              <ClarifyingNotesSection consultationId={note.id} canAdd={canAddAddendum} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-1">
        {label}
      </p>
      <p className="text-sm text-ml-on-surface whitespace-pre-wrap">{value}</p>
    </div>
  )
}
